/**
 * 保存プロトコルの判定ロジック（docs/02-architecture.md §6、ADR-003）。
 *
 * DB アクセスから切り離した純粋関数として置く。CLAUDE.md により、この判定は
 * ユニットテスト必須。Worker 側の PUT /api/notes/:id はこの決定に従うだけにする。
 *
 * 不変条件: 入力済みのテキストは、いかなる障害でも失われない。
 * 自動保存はヘッドを黙って上書きするが、上書きで失われる内容が生じ得る場面
 * （別セッション・長時間のアイドル）では必ず先にリビジョンへ退避する。
 */

import type { JstDate, RevisionReason } from './types';

/** アイドルとみなす無操作時間。これを超えて内容変更があれば執筆セッションの区切りとみなす。 */
export const IDLE_REVISION_MS = 45 * 60 * 1000;

/**
 * local ID の新規ノートをサーバーへ作成してよいか。
 *
 * trim は判定にだけ使い、実際に保存する文字列は呼び出し側がそのまま保持する。
 * カテゴリや対象日は作成条件に含めない（docs/01-requirements.md §5.1）。
 */
export function shouldCreateNewNote(title: string | null, body: string): boolean {
  return (title?.trim() ?? '') !== '' || body.trim() !== '';
}

/** 保存判定に必要なヘッドの状態。 */
export interface HeadStateForSave {
  version: number;
  last_session_id: string | null;
  updated_at: string;
  deleted_at: string | null;
  title: string | null;
  body: string;
  category_id: number | null;
  subject_date: JstDate;
  subject_date_end: JstDate | null;
}

/** 保存リクエストのうち判定に使う部分。 */
export interface SaveRequestForDecision {
  base_version: number;
  session_id: string;
  title: string | null;
  body: string;
  category_id: number | null;
  subject_date: JstDate;
  subject_date_end: JstDate | null;
}

export type SaveDecision =
  | {
      kind: 'rejected';
      /** ゴミ箱内のノートは編集できない。復元を促す。 */
      reason: 'trashed';
    }
  | {
      kind: 'accepted';
      /** null でなければ、上書き前のヘッドをこの理由でリビジョンへ退避する。 */
      retreatReason: RevisionReason | null;
      /** 古い版に基づく保存だったか。true のときだけ控えめに通知する。 */
      regression: boolean;
      /** 書き込むべき新しい版番号。 */
      nextVersion: number;
    };

/** 保存対象のフィールドに実質的な変更があるか。 */
export function hasContentChange(
  head: HeadStateForSave,
  req: SaveRequestForDecision,
): boolean {
  return (
    head.body !== req.body ||
    (head.title ?? null) !== (req.title ?? null) ||
    head.category_id !== req.category_id ||
    head.subject_date !== req.subject_date ||
    (head.subject_date_end ?? null) !== (req.subject_date_end ?? null)
  );
}

/**
 * PUT /api/notes/:id の判定。
 *
 * 退避の条件は 2 つだけで、順序が意味を持つ:
 *  1. 別セッションからの上書き → 'session_boundary'
 *     （他端末・他タブの内容を黙って消さない。ADR-003 の「何も失わない」保証の本体）
 *  2. 同一セッションでも最終更新から IDLE_REVISION_MS 以上空いていて内容変更がある → 'idle'
 *     （一晩置いてから書き足す場合に、前夜の版を残す）
 *
 * 同一セッション内の連続自動保存は何百回来ても退避しない。
 */
export function decideSave(
  head: HeadStateForSave,
  req: SaveRequestForDecision,
  nowMs: number,
): SaveDecision {
  if (head.deleted_at !== null) {
    return { kind: 'rejected', reason: 'trashed' };
  }

  let retreatReason: RevisionReason | null = null;

  if (req.session_id !== head.last_session_id) {
    // 初回保存（last_session_id が null）でも、ヘッドに内容があるなら退避する。
    // 空のヘッド（作成直後）を退避しても履歴が無意味に増えるだけなので除く。
    if (head.last_session_id !== null || hasContentChange(head, req)) {
      retreatReason = 'session_boundary';
    }
  } else {
    const updatedMs = Date.parse(head.updated_at);
    const idle = !Number.isNaN(updatedMs) && nowMs - updatedMs > IDLE_REVISION_MS;
    if (idle && hasContentChange(head, req)) {
      retreatReason = 'idle';
    }
  }

  return {
    kind: 'accepted',
    retreatReason,
    // base_version がヘッドより古い＝この保存は他の保存を追い越して古い内容を書く。
    regression: req.base_version < head.version,
    nextVersion: head.version + 1,
  };
}

/**
 * ドラフト復元の判定（docs/02-architecture.md §5 層1）。
 *
 * ノートを開いたとき、ローカルのドラフトとサーバーのヘッドのどちらを見せるかを決める。
 * ヘッドを勝手に巻き戻さないことと、孤児ドラフトを捨てないことの両立が要点。
 */
export interface DraftStateForDecision {
  base_version: number;
  body: string;
  title: string | null;
  category_id: number | null;
  subject_date: JstDate;
  subject_date_end: JstDate | null;
}

export type DraftDecision =
  /** ドラフトは古いか同内容。破棄してヘッドを表示する。 */
  | { kind: 'discard_draft' }
  /** ドラフトをエディタへ黙って復元し、未保存としてマークする。 */
  | { kind: 'restore_draft' }
  /**
   * ドラフトの base_version がヘッドより古い（他端末が先行した）。
   * ヘッドを表示しつつ、孤児ドラフトを reason='draft_recovery' でサーバー履歴へ退避し、
   * ローカルからは破棄して控えめに通知する。
   */
  | { kind: 'rescue_orphan_draft' };

export function decideDraftRestore(
  draft: DraftStateForDecision | null,
  head: Pick<HeadStateForSave, 'version' | 'title' | 'body' | 'category_id' | 'subject_date' | 'subject_date_end'>,
): DraftDecision {
  if (!draft) return { kind: 'discard_draft' };

  const differs =
    draft.body !== head.body ||
    (draft.title ?? null) !== (head.title ?? null) ||
    draft.category_id !== head.category_id ||
    draft.subject_date !== head.subject_date ||
    (draft.subject_date_end ?? null) !== (head.subject_date_end ?? null);

  if (!differs) return { kind: 'discard_draft' };

  if (draft.base_version < head.version) {
    // 他端末が先に進めている。ドラフトを適用するとヘッドを巻き戻してしまう。
    return { kind: 'rescue_orphan_draft' };
  }
  return { kind: 'restore_draft' };
}
